<div class="platform-workspaces-container">
  <div class="header-title">Workspace Management</div>
  <p class="description">
    Manage your workspaces and monitor resource usage. Delete workspaces that
    are no longer needed.
  </p>

  @if (loading) {
    <app-loading-overlay></app-loading-overlay>
  }

  @if (!loading && loadError) {
    <div class="error-message">
      Unable to load workspaces. Please try again.
      <button mat-stroked-button color="primary" (click)="loadWorkspaces()">
        Retry
      </button>
    </div>
  }

  @if (!loading && !loadError && workspaces.length === 0) {
    <div class="empty-message">No workspaces found.</div>
  }

  @if (!loading && !loadError && workspaces.length > 0) {
    <!-- Dashboard Section -->
    <div class="dashboard-section">
      <!-- Summary Stats -->
      <div class="stats-row">
        <mat-card class="stat-card">
          <div class="stat-value">{{ totalWorkspaces }}</div>
          <div class="stat-label">Total Workspaces</div>
        </mat-card>
        <mat-card class="stat-card">
          <div class="stat-value">{{ formatBytes(totalPostgresSize) }}</div>
          <div class="stat-label">PostgreSQL Storage</div>
        </mat-card>
        <mat-card class="stat-card">
          <div class="stat-value">
            {{ formatBytes(totalElasticsearchSize) }}
          </div>
          <div class="stat-label">Elasticsearch Storage</div>
        </mat-card>
      </div>

      <!-- Charts Row -->
      <div class="charts-row">
        <mat-card class="chart-card">
          <mat-card-header>
            <mat-card-title>Workspaces by Owner</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div
              echarts
              [options]="ownerChartOptions"
              class="chart-container"
            ></div>
          </mat-card-content>
        </mat-card>

        <mat-card class="chart-card">
          <mat-card-header>
            <mat-card-title>PostgreSQL Storage (Top 10)</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div
              echarts
              [options]="postgresChartOptions"
              class="chart-container"
            ></div>
          </mat-card-content>
        </mat-card>

        <mat-card class="chart-card">
          <mat-card-header>
            <mat-card-title>Elasticsearch Storage (Top 10)</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <div
              echarts
              [options]="elasticsearchChartOptions"
              class="chart-container"
            ></div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>

    <!-- Table Section -->
    <mat-card class="table-card">
      <mat-card-header>
        <mat-card-title>All Workspaces</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="table-container">
          <table
            mat-table
            [dataSource]="workspaces"
            class="workspaces-table"
            matSort
            (matSortChange)="sortData($event)"
            aria-label="Platform workspaces table"
          >
            <!-- Name Column -->
            <ng-container matColumnDef="name">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                [matTooltip]="'Name'"
              >
                <span class="header-text">Name</span>
              </th>
              <td mat-cell *matCellDef="let workspace">{{ workspace.name }}</td>
            </ng-container>

            <!-- Owner Email Column -->
            <ng-container matColumnDef="owner_email">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                [matTooltip]="'Owner'"
              >
                <span class="header-text">Owner</span>
              </th>
              <td mat-cell *matCellDef="let workspace">
                {{ workspace.owner_email }}
              </td>
            </ng-container>

            <!-- Data Source Count Column -->
            <ng-container matColumnDef="datasource_count">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                [matTooltip]="'Data Sources'"
              >
                <span class="header-text">Data Sources</span>
              </th>
              <td mat-cell *matCellDef="let workspace">
                {{ workspace.datasource_count }}
              </td>
            </ng-container>

            <!-- Model Count Column -->
            <ng-container matColumnDef="model_count">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                [matTooltip]="'Models'"
              >
                <span class="header-text">Models</span>
              </th>
              <td mat-cell *matCellDef="let workspace">
                {{ workspace.model_count }}
              </td>
            </ng-container>

            <!-- Member Count Column -->
            <ng-container matColumnDef="member_count">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                [matTooltip]="'Members'"
              >
                <span class="header-text">Members</span>
              </th>
              <td mat-cell *matCellDef="let workspace">
                {{ workspace.member_count }}
              </td>
            </ng-container>

            <!-- PostgreSQL Size Column -->
            <ng-container matColumnDef="postgres_size_bytes">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                [matTooltip]="'PostgreSQL Size'"
              >
                <span class="header-text">PostgreSQL Size</span>
              </th>
              <td mat-cell *matCellDef="let workspace">
                {{ formatBytes(workspace.postgres_size_bytes) }}
              </td>
            </ng-container>

            <!-- Elasticsearch Size Column -->
            <ng-container matColumnDef="elasticsearch_size_bytes">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                [matTooltip]="'Elasticsearch Size'"
              >
                <span class="header-text">Elasticsearch Size</span>
              </th>
              <td mat-cell *matCellDef="let workspace">
                {{ formatBytes(workspace.elasticsearch_size_bytes) }}
              </td>
            </ng-container>

            <!-- Most Recent API Request Column -->
            <ng-container matColumnDef="most_recent_api_request">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                [matTooltip]="'Last API Request'"
              >
                <span class="header-text">Last API Request</span>
              </th>
              <td mat-cell *matCellDef="let workspace">
                {{ formatDate(workspace.most_recent_api_request) }}
              </td>
            </ng-container>

            <!-- Actions Column -->
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef [matTooltip]="'Actions'">
                Actions
              </th>
              <td mat-cell *matCellDef="let workspace">
                <button
                  mat-icon-button
                  color="warn"
                  [disabled]="!isOwner(workspace)"
                  (click)="deleteWorkspace(workspace)"
                  [matTooltip]="
                    isActualOwner(workspace)
                      ? 'Delete workspace'
                      : isPlatformAdmin
                        ? 'Delete workspace as platform admin'
                        : 'Only the workspace owner can delete'
                  "
                  [attr.aria-label]="'Delete workspace ' + workspace.name"
                >
                  <ng-icon name="HeroTrash" class="delete-icon"></ng-icon>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
          </table>
        </div>
      </mat-card-content>
    </mat-card>
  }
</div>
